import _ from "lodash"
import {
  Polygon,
  MultiPolygon,
  LineString,
  Marker,
  Coordinate,
  MultiLineString,
  ui,
} from "maptalks"
import turfCenter from "@turf/center"
import turfBuffer from "@turf/buffer"
import { feature, lineString } from "@turf/helpers"

import {
  SpriteMaterial,
  MeshLambertMaterial,
  AmbientLight,
  DirectionalLight,
  CanvasTexture,
  TextureLoader,
  ClampToEdgeWrapping,
} from "three"
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader"

import { GLTFMarker } from "@maptalks/gltf-layer"

import {
  GroundLabel,
  SpriteMarker,
  Billboard,
  NavigationPath,
} from "../object3d"
import { getCenterFromGeometry, createPolygonFromLineString } from "./geometry"
import {
  createSVGPathFromMarkerSymbol,
  loadImage,
  loadSvgAsImage,
  buildPlainSvg,
  drawCenteredImage,
} from "./svg"
import {
  DEFAULT_EXTRUDE_CONFIGS,
  SUFFIX_MARKER,
  getSectionMarkerSymbolKey,
  ORIGIN_MARKER_DIRECTIONS,
  ORIGIN_MARKER_SPRITE_STATES,
  ORIGIN_MARKER_SPRITE_SHEET_COLUMNS,
  ORIGIN_MARKER_SPRITE_SHEET_ROWS,
} from "../constants"
import { LineMaterial } from "maptalks.three"
import cleanCoords from "@turf/clean-coords"

const GeometryType = {
  Polygon,
  MultiPolygon,
}

const ORDINAL_HEIGHT = 0 // in version 3D change ORDINAL_HEIGHT for set altitude of geometry

const VENUE_Z_INDEX = 0

const LEVEL_Z_INDEX = 2
const UNIT_Z_INDEX = 3
const FIXTURE_Z_INDEX = 4
const DECORATION_Z_INDEX = 4
const KIOSK_Z_INDEX = 5
const OPENING_Z_INDEX = 3
const SECTION_Z_INDEX = 6
const USER_LOCATION_Z_INDEX = 99
const LAST_LOCATION_Z_INDEX = USER_LOCATION_Z_INDEX - 1

const PREFIX_HIGHLIGHTED_SYMBOL_KEY = "highlighted"
const SPRITE_MARKER_FEATURE = [
  // Amenity
  "amenity-atm",
  "amenity-babychanging",
  "amenity-strollerrental",
  "amenity-boardinggate.ferry",
  "amenity-elevator",
  "amenity-escalator",
  "amenity-stairs",
  "amenity-information",
  "amenity-information.transit",
  "amenity-reception.desk",
  "amenity-wheelchair",
  "amenity-restroom",
  "amenity-restroom.male",
  "amenity-restroom.female",
  "amenity-restroom.wheelchair",
  "amenity-restroom.family",
  "amenity-restroom.unisex",
  "amenity-shower",
  "amenity-taxi",
  "amenity-groundtransportation",
  "amenity-bus",
  "amenity-bus.muni",
  "amenity-shuttle",
  "amenity-transit",
  "amenity-pedestriancrossing",
  "amenity-parking",
  "amenity-parking.shortterm",
  "amenity-parking.longterm",
  "amenity-parking.waitingarea",
  "amenity-parking.compact",
  "amenity-parking.ev",
  "amenity-parking.bicycle",
  "amenity-parking.motorcycle",
  "amenity-changemachine",
  "amenity-animalreliefarea",
  "amenity-privatelounge",
  "amenity-rideshare",
  "amenity-valet",
  "amenity-landmark",
  "amenity-rail.muni",
  "amenity-service",
  "amenity-smokingarea",
  "amenity-ticketing",
  "amenity-meetingpoint",
  "amenity-prayerroom",
  "amenity.prayerroom.islam.female",
  "amenity.prayerroom.islam.male",
  "amenity-firstaid",
  "amenity-ticketing.rail",
  "amenity-exhibit",
  "amenity-mothersroom",
  "amenity-checkin.desk",
  "amenity-baggagestorage",
  "amenity-baggagecarts",
  "amenity-library",
  "amenity-coinlocker",
  "amenity-powerchargingstation",
  "amenity-wheelchairassit",
  "amenity-lostandfound",
  "amenity-foodservice",
  "amenity-vendingmachine",
  "amenity-businesscenter",
  "amenity-guestservices",
  "amenity-emergencyshelter",
  "amenity-prayerroom.buddhism",
  "amenity-prayerroom.islam.female",
  "amenity-prayerroom.islam.male",
  "amenity-defibrillator",
  "amenity-drinkingfountain",
  "amenity-handsanitizerstation",
  "amenity-unspecified",
  "amenity-entry",
  "amenity-fireextinguisher",
  "amenity-security",
  "amenity-seating",
  "amenity-recreation",
  "amenity-movingwalkway",
  "amenity-copymachine",
  "amenity-fieldofplay",
  "amenity-fieldofplay.soccer",
  "amenity-fieldofplay.tennis",
  "amenity-amphitheater",
  "amenity-hoteling",
  // Occupant
  "occupant-currencyexchange",
  "occupant-donationcenter",
  "occupant-bank",
  "occupant-books",
  "occupant-postoffice",
  // Opening
  "opening-emergencyexit",
  // Section materials are built lazily per (category × priority) via getSectionMaterial
]

const SPRITE_HIGHLIGHT_MARKER_FEATURE = SPRITE_MARKER_FEATURE.map(
  (featCate) =>
    `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${featCate.split("-").join(".")}`
)

const OCCUPANT_TEXT_MARKER_CLASSNAME = "mtk-occupant-text-marker"
const SECTION_TEXT_MARKER_CLASSNAME = "mtk-section-text-marker"

export const buildCompositeId = (uIds = [], options = {}) => {
  const { separator = "_", suffix = null, prefix = null } = options
  const combinedId = _.compact([prefix, ...uIds, suffix]).join(separator)
  return combinedId
}

const getAltitude = (properties) =>
  Math.max(0, properties.ordinal * ORDINAL_HEIGHT || 0)

const createRoomUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)

  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}
const createAmenityUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  const area = new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
  return area
}

const createNonpublicUnit = (feature, style, options = {}) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}
const createWalkwayUnit = (feature, style, options) => {
  const { geometry, id, properties } = feature
  const { allowOverride, inheritFillColorToLine } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      id,
      feature_type: "unit",
      category: properties.category,
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
  })
}

const createWaterFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
    zIndex,
  })
}

const createVegetationFixture = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: {
      ...symbolStyle,
    },
    zIndex,
  })
}

const createObstructionalFixture = (feature, style = {}, options = {}) => {
  const { geometry, properties = {} } = feature
  const { model3d } = properties
  const { allowOverride, inheritFillColorToLine, zIndex } = options
  const symbolStyle = { ...style }
  const isLineString = geometry?.type === "LineString"
  if (!_.isEmpty(model3d)) return
  if (allowOverride) {
    _.merge(symbolStyle, properties.style)
    // If geometry is a LineString (to be converted to Polygon), add polygonFill with lineColor
    if (isLineString) symbolStyle.polygonFill = symbolStyle.lineColor
  }
  if (inheritFillColorToLine) {
    symbolStyle.lineColor = isLineString
      ? symbolStyle.lineColor
      : symbolStyle.polygonFill
  }
  //create polygon with line-offset if geometry is lineString
  if (isLineString) {
    const polygon = createPolygonFromLineString(geometry)

    return new GeometryType["Polygon"](polygon, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: symbolStyle,
      defaultSymbol: symbolStyle,
      zIndex,
    })
  }
  return new GeometryType[geometry.type](geometry.coordinates, {
    properties: {
      altitude: getAltitude(properties),
    },
    symbol: symbolStyle,
    defaultSymbol: symbolStyle,
    zIndex,
  })
}

const creatingDefaultSection = (feature, style = {}, options = {}) => {
  const { geometry, properties } = feature

  const {
    allowOverride,
    inheritFillColorToLine,
    zIndex = SECTION_Z_INDEX,
  } = options

  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)
  if (inheritFillColorToLine) symbolStyle.lineColor = symbolStyle.polygonFill

  try {
    return new GeometryType[geometry.type](geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: symbolStyle,
      defaultSymbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.warn(`Error creatingDefaultSection`, geometry.type)
  }
}

const createVegetationSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const creatingSeatingSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const createEatingDrinkingSection = (feature, style = {}, options = {}) => {
  return creatingDefaultSection(feature, style, options)
}

const createPrincipalOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating pedestrian principal opening:`, feature)
  }
}

const createEmergencyExitOpening = (feature, style, options = {}) => {
  const { geometry, properties } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }
  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        altitude: getAltitude(properties),
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating emergency exit opening:`, feature)
  }
}
const createPedestrianOpening = (feature, style, options = {}) => {
  const { geometry, properties, id } = feature
  const { allowOverride, zIndex } = options
  const symbolStyle = { ...style }

  if (allowOverride) _.merge(symbolStyle, properties.style)

  try {
    return new LineString(geometry.coordinates, {
      properties: {
        id,
        feature_type: "opening",
        category: properties.category,
        altitude: getAltitude(properties),
      },
      symbol: symbolStyle,
      zIndex,
    })
  } catch (error) {
    console.log(`error creating pedestrian opening:`, feature)
  }
}

const loadModel3d = (model3d, coordinate, threeLayer) => {
  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader()
    const { url, properties: modelProperties } = model3d
    loader.load(
      url,
      (gltf) => {
        const object3d = gltf.scene
        // จัดขนาด + scale ที่เหมาะสม
        object3d.rotation.x = _.get(modelProperties, "rotation.x")
        object3d.rotation.y = _.get(modelProperties, "rotation.y")
        object3d.scale.set(...(_.get(modelProperties, "scale") || []))

        const object = threeLayer.toModel(object3d, {
          coordinate,
        })

        object.getObject3d().traverse((child) => {
          if (child.isMesh === true) {
            child.material.transparent = true
            child.material.metalness = 0.1 // set to near 0 because metal requires environment to reflect light to, if there's nothing then metal will become black.
          }
        })

        resolve(object)
      },
      (xhr) => {},
      (error) => {
        reject(error)
      }
    )
  })
}

const create3DModels = async (
  models,
  defaultCoordinate,
  properties,
  threeLayer
) => {
  let modelObjs = []
  for (let j = 0; j < models.length; j++) {
    const model = models[j]
    const positionCoord = _.get(model, "properties.position")
    const coord = positionCoord || defaultCoordinate
    const object = await loadModel3d(model, coord, threeLayer)
    object.properties = properties
    modelObjs.push(object)
  }

  return modelObjs
}

const create3DBillboard = (billboard, threeLayer) => {
  const { id, feature_type, properties } = billboard
  const {
    logo,
    altitude,
    scale,
    alphaTest,
    legColor,
    showLeg,
    is_clickable,
    sizeAttenuation,
    units,
    altitudeFactor,
    displayOffset,
  } = properties

  const amenityLocatedUnit = _.first(units)
  const { category, extrude: extrudeFromProperties } =
    amenityLocatedUnit?.properties

  const extrude =
    extrudeFromProperties ?? DEFAULT_EXTRUDE_CONFIGS.unit[category]

  const coordinates = getCenterFromGeometry(billboard.geometry)
  const billboardProperties = {
    ...billboard.properties,
    id,
    feature_type,
  }

  const options = {
    altitude,
    extrude,
    scale,
    alphaTest,
    legColor,
    showLeg,
    interactive: is_clickable,
    sizeAttenuation,
    altitudeFactor,
    renderOrder: 5,
    displayOffset,
  }

  return new Billboard(
    coordinates,
    options,
    logo?.url,
    threeLayer,
    billboardProperties
  )
}

const create3DAmenityMarker = (feature, threeLayer, config) => {
  const { geometry, properties, feature_type, id } = feature
  const { category, units, kiosk, is_clickable } = properties
  const amenityLocatedUnit = _.first(units)
  const isLocatedUnitModel3dAvailable = !_.isEmpty(
    amenityLocatedUnit?.properties?.model3d
  )
  const isLocatedKioskModel3dAvailable = !_.isEmpty(kiosk?.properties?.model3d)
  const {
    spriteMarkerMaterialObj,
    spriteHighlightMarkerOptionObj,
    spriteMarkerOptionObj,
  } = config ?? {}

  const unitConfig = getExtrudeConfigByFeature(config, amenityLocatedUnit)
  const unitHeight = isLocatedUnitModel3dAvailable
    ? 0
    : _.get(unitConfig, "height", 0)
  const kioskConfig = getExtrudeConfigByFeature(config, kiosk)
  const kioskHeight = isLocatedKioskModel3dAvailable
    ? 0
    : _.get(kioskConfig, "height", 0)

  const coordinates = _.get(geometry, "coordinates")

  const markerProperties = {
    ...properties,
    coordinates,
    id,
    feature_type,
  }

  const material = _.get(spriteMarkerMaterialObj, `${feature_type}.${category}`)

  const markerOptions = _.get(
    spriteMarkerOptionObj,
    `${feature_type}.${category}`
  )
  const highlightOptions = _.get(
    spriteHighlightMarkerOptionObj,
    `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
  )

  const options = {
    scale: markerOptions.scale || 0.05,
    altitude: unitHeight + kioskHeight + (markerOptions.altitudeOffset ?? 0),
    highlight: highlightOptions,
    interactive: is_clickable,
    renderOrder: 5,
    anchor: markerOptions?.markerAnchor,
    offsetAltitude: markerOptions?.offsetAltitude,
  }

  return create3DMarker(
    coordinates,
    options,
    material,
    threeLayer,
    markerProperties
  )
}

const create3DSectionIconMarker = (feature, threeLayer, config) => {
  const { properties, feature_type, id } = feature
  const { category, is_clickable, display_point } = properties

  const {
    material: materialOverride,
    highlightMaterial: highlightMaterialOverride,
    spriteMarkerMaterialObj,
    spriteHighlightMarkerOptionObj,
    spriteMarkerOptionObj,
    altitude,
    sectionAltitude,
    renderOrder = 5,
    baseSymbol,
    iconSymbol,
    highlightBaseSymbol,
    highlightIconSymbol,
    visible,
  } = config ?? {}

  const coordinates =
    _.get(display_point, "coordinates") ||
    getCenterFromGeometry(feature.geometry)

  const markerProperties = {
    ...properties,
    coordinates,
    id,
    feature_type,
    altitude,
    sectionAltitude,
  }

  const material =
    materialOverride ??
    _.get(spriteMarkerMaterialObj, `${feature_type}.${category}`)

  const markerOptions = _.get(
    spriteMarkerOptionObj,
    `${feature_type}.${category}`
  )
  const highlightOptions = _.get(
    spriteHighlightMarkerOptionObj,
    `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
  )

  const SECTION_REFERENCE_MARKER_WIDTH = 48
  const SECTION_REFERENCE_SCALE = 0.05
  const scaleX =
    baseSymbol?.markerWidth != null
      ? (baseSymbol.markerWidth / SECTION_REFERENCE_MARKER_WIDTH) *
        SECTION_REFERENCE_SCALE
      : markerOptions?.scale ?? SECTION_REFERENCE_SCALE
  const aspectY =
    baseSymbol?.markerPathWidth && baseSymbol?.markerPathHeight
      ? baseSymbol.markerPathHeight / baseSymbol.markerPathWidth
      : 1

  // Mirrors createSpriteMaterialByLabelSymbol's box/shadow math so scale
  // agrees with the baked texture.
  const shadowMetrics = getIconCompositeShadowMetrics(baseSymbol, iconSymbol)
  const scale = {
    x: scaleX * shadowMetrics.scaleCompensation.x,
    y: scaleX * aspectY * shadowMetrics.scaleCompensation.y,
  }

  const highlightBaseMarkerWidth = highlightBaseSymbol?.markerWidth
  const highlightScaleX =
    highlightBaseMarkerWidth != null
      ? (highlightBaseMarkerWidth / SECTION_REFERENCE_MARKER_WIDTH) *
        SECTION_REFERENCE_SCALE
      : scaleX * HIGHLIGHT_SCALE_FACTOR
  const highlightAspectY =
    highlightBaseSymbol?.markerPathWidth &&
    highlightBaseSymbol?.markerPathHeight
      ? highlightBaseSymbol.markerPathHeight /
        highlightBaseSymbol.markerPathWidth
      : aspectY
  const highlightShadowMetrics = getIconCompositeShadowMetrics(
    highlightBaseSymbol,
    highlightIconSymbol
  )
  const highlightScale = {
    x: highlightScaleX * highlightShadowMetrics.scaleCompensation.x,
    y:
      highlightScaleX *
      highlightAspectY *
      highlightShadowMetrics.scaleCompensation.y,
  }

  const requestedAnchor =
    baseSymbol?.markerAnchor ?? markerOptions?.markerAnchor ?? "bottom"
  // Symmetric padding only displaces a "bottom" anchor (else it'd point at
  // the padded edge, not the bubble tip).
  const anchor =
    shadowMetrics.hasShadow && requestedAnchor === "bottom"
      ? { x: 0.5, y: shadowMetrics.bottomAnchorY }
      : requestedAnchor

  const options = {
    scale,
    altitude,
    // SpriteMarker defaults alphaTest to 0.3, which would clip a soft
    // shadow's low alpha.
    alphaTest: shadowMetrics.hasShadow ? 0.02 : undefined,
    highlight: _.merge({}, highlightOptions, {
      options: {
        scale: highlightScale,
        altitude: altitude + (sectionAltitude ?? 0) + 2,
      },
      ...(highlightMaterialOverride && { material: highlightMaterialOverride }),
    }),
    interactive: is_clickable,
    renderOrder,
    visible,
    anchor,
    offsetAltitude: baseSymbol?.offsetAltitude ?? markerOptions?.offsetAltitude,
  }

  return create3DMarker(
    coordinates,
    options,
    material,
    threeLayer,
    markerProperties
  )
}

const HIGHLIGHT_SCALE_FACTOR = 1.25

const create3DSectionLogoMarker = (feature, threeLayer, config) => {
  const {
    altitude,
    sectionAltitude,
    logoUrl,
    renderOrder = 5,
    spriteMarkerOptionObj,
    baseSymbol,
    logoSymbol,
    highlightBaseSymbol,
    highlightLogoSymbol,
  } = config ?? {}

  const { markerDx = 0, markerDy = 0 } = logoSymbol ?? {}
  const logoScale = getMarkerScaleRatio(logoSymbol, baseSymbol)

  const { properties, feature_type, id } = feature
  const { category, is_clickable } = properties

  const coordinates =
    _.get(properties, "display_point.coordinates") ||
    getCenterFromGeometry(feature.geometry)

  if (!coordinates) {
    console.log("[create3DSectionLogoMarker]: no coordinates", { id })
    return
  }

  const SECTION_LOGO_REFERENCE_MARKER_WIDTH = 48
  const SECTION_LOGO_REFERENCE_SCALE = 0.05

  const markerOptions = _.get(
    spriteMarkerOptionObj,
    `${feature_type}.${category}`
  )
  const baseMarkerWidth = baseSymbol?.markerWidth
  const scaleX =
    baseMarkerWidth != null
      ? (baseMarkerWidth / SECTION_LOGO_REFERENCE_MARKER_WIDTH) *
        SECTION_LOGO_REFERENCE_SCALE
      : markerOptions?.scale ?? SECTION_LOGO_REFERENCE_SCALE
  const aspectY =
    baseSymbol?.markerPathWidth && baseSymbol?.markerPathHeight
      ? baseSymbol.markerPathHeight / baseSymbol.markerPathWidth
      : 1

  // A baked shadow pads the texture beyond its nominal box; compensate
  // scale/anchor so the bubble doesn't shift or shrink.
  const shadowMetrics = getSectionLogoShadowMetrics(baseSymbol)
  const scale = {
    x: scaleX * shadowMetrics.scaleCompensation.x,
    y: scaleX * aspectY * shadowMetrics.scaleCompensation.y,
  }

  const material = createSectionLogoCompositeMaterial(baseSymbol, logoUrl, {
    offsetX: markerDx,
    offsetY: markerDy,
    ...(logoScale != null && { scale: logoScale }),
  })

  const highlightBaseMarkerWidth = highlightBaseSymbol?.markerWidth
  const highlightScaleX =
    highlightBaseMarkerWidth != null
      ? (highlightBaseMarkerWidth / SECTION_LOGO_REFERENCE_MARKER_WIDTH) *
        SECTION_LOGO_REFERENCE_SCALE
      : scaleX * HIGHLIGHT_SCALE_FACTOR
  const highlightAspectY =
    highlightBaseSymbol?.markerPathWidth &&
    highlightBaseSymbol?.markerPathHeight
      ? highlightBaseSymbol.markerPathHeight /
        highlightBaseSymbol.markerPathWidth
      : aspectY
  const highlightShadowMetrics =
    getSectionLogoShadowMetrics(highlightBaseSymbol)
  const highlightScale = {
    x: highlightScaleX * highlightShadowMetrics.scaleCompensation.x,
    y:
      highlightScaleX *
      highlightAspectY *
      highlightShadowMetrics.scaleCompensation.y,
  }

  const buildHighlightMaterial = highlightBaseSymbol
    ? () => {
        const { markerDx: hDx = 0, markerDy: hDy = 0 } =
          highlightLogoSymbol ?? {}
        const hLogoScale = getMarkerScaleRatio(
          highlightLogoSymbol,
          highlightBaseSymbol
        )
        return createSectionLogoCompositeMaterial(
          highlightBaseSymbol,
          logoUrl,
          {
            offsetX: hDx,
            offsetY: hDy,
            ...(hLogoScale != null && { scale: hLogoScale }),
          }
        )
      }
    : null

  const requestedAnchor =
    baseSymbol?.markerAnchor ?? markerOptions?.markerAnchor ?? "bottom"
  // Symmetric padding only displaces a "bottom" anchor (else it'd point at
  // the padded edge, not the bubble tip).
  const anchor =
    shadowMetrics.hasShadow && requestedAnchor === "bottom"
      ? { x: 0.5, y: shadowMetrics.bottomAnchorY }
      : requestedAnchor

  const options = {
    scale,
    altitude,
    interactive: is_clickable,
    renderOrder,
    // SpriteMarker defaults alphaTest to 0.3, which would clip a soft
    // shadow's low alpha.
    alphaTest: shadowMetrics.hasShadow ? 0.02 : undefined,
    highlight: {
      options: {
        scale: highlightScale,
        altitude: altitude + (sectionAltitude ?? 0) + 2,
      },
      material: buildHighlightMaterial,
    },
    anchor,
    offsetAltitude: baseSymbol?.offsetAltitude ?? markerOptions?.offsetAltitude,
  }

  const markerProperties = {
    ...properties,
    coordinates,
    id,
    feature_type,
    altitude,
    sectionAltitude,
  }

  return create3DMarker(
    coordinates,
    options,
    material,
    threeLayer,
    markerProperties
  )
}

const createExtrudePolygon = (
  geometry,
  threeLayer,
  material,
  height,
  properties = {},
  options
) => {
  const {
    offset = 0,
    altitude = 0,
    bottomHeight = 0,
    topOutline = false,
    bottomOutline = false,
    outlineColor,
    linewidth,
    visible = true,
    renderOrder,
    interactive = true,
  } = options
  const meshs = []

  // If the geometry cannot be offset, fallback to the original geometry
  const offsetGeometry =
    turfBuffer(geometry, offset, { units: "meters" }) || feature(geometry)

  const object = threeLayer.toExtrudePolygon(
    offsetGeometry,
    {
      height,
      async: true,
      altitude,
      bottomHeight: bottomHeight ?? 0,
      defaultAltitude: altitude,
      interactive,
    },
    material
  )
  object.properties = properties
  meshs.push(object)

  if (topOutline || bottomOutline) {
    // TODO: support line material overrides from map theme
    const [outerRing, ...innerRings] =
      offsetGeometry?.geometry?.coordinates ?? []

    const lineMaterial = new LineMaterial({
      color: outlineColor ?? "#2A2A2A",
      linewidth: linewidth ?? 1,
      transparent: true,
      visible,
    })

    const lineStrings = [
      new LineString(outerRing),
      ...innerRings.map((innerRing) => new LineString(innerRing)),
    ]

    const buildOutline = (outlineBottomHeight) => {
      const lines = threeLayer.toFatLines(
        lineStrings,
        {
          altitude,
          bottomHeight: outlineBottomHeight,
          interactive: false,
        },
        lineMaterial
      )

      lines.properties = properties
      meshs.push(lines)
    }

    if (topOutline) {
      /**
       * NOTE:
       * lift the outline above the polygon's top face to avoid z-fighting
       * between the top outline and the extruded polygon's top face.
       */
      buildOutline(bottomHeight + height + 0.001)
    }

    if (bottomOutline) {
      buildOutline(bottomHeight)
    }
  }

  if (renderOrder) {
    meshs.forEach((mesh) => {
      mesh.getObject3d().renderOrder = renderOrder
    })
  }

  return meshs
}

const createExtrudeLine = (
  geometry,
  threeLayer,
  material,
  height,
  properties = {},
  options
) => {
  const { altitude = 0, bottomHeight } = options
  // If the geometry cannot be offset, fallback to the original geometry
  const line = new LineString(geometry.coordinates)
  const object = threeLayer.toExtrudeLine(
    line,
    {
      height,
      width: properties?.defaultLineWidth ?? 0.5,
      async: true,
      altitude,
      bottomHeight: bottomHeight ?? 0,
      defaultAltitude: altitude,
    },
    material
  )
  object.properties = properties
  return object
}

const create3DMarker = (
  coordinates,
  options,
  material,
  threeLayer,
  markerProperties
) => {
  return new SpriteMarker(
    coordinates,
    options,
    material,
    threeLayer,
    markerProperties
  )
}

export const applyOriginMarkerSpriteFrame = (texture, frameIndex) => {
  if (!texture) return
  const cols = ORIGIN_MARKER_SPRITE_SHEET_COLUMNS
  const rows = ORIGIN_MARKER_SPRITE_SHEET_ROWS
  const col = frameIndex % cols
  const row = Math.floor(frameIndex / cols)
  texture.repeat.set(1 / cols, 1 / rows)
  texture.offset.set(col / cols, 1 - (row + 1) / rows)
}

const directionalSpriteMaterialsCache = new Map()
const getOriginMarkerDirectionalSpriteMaterials = (spriteUrls = {}) => {
  const cacheKey = JSON.stringify(spriteUrls)
  if (directionalSpriteMaterialsCache.has(cacheKey)) {
    return directionalSpriteMaterialsCache.get(cacheKey)
  }
  const loader = new TextureLoader()
  const materials = ORIGIN_MARKER_SPRITE_STATES.reduce((stateAcc, state) => {
    stateAcc[state] = ORIGIN_MARKER_DIRECTIONS.reduce((acc, direction) => {
      const material = new SpriteMaterial({
        transparent: true,
        alphaTest: 0.3,
        depthTest: false,
      })
      const url = spriteUrls[state]?.[direction]
      if (url) {
        loader.load(
          url,
          (texture) => {
            texture.wrapS = ClampToEdgeWrapping
            texture.wrapT = ClampToEdgeWrapping
            applyOriginMarkerSpriteFrame(texture, 0)
            material.map = texture
            material.needsUpdate = true
          },
          undefined,
          (error) =>
            console.warn(
              `Error loading origin marker sprite for ${state}/${direction}:`,
              error
            )
        )
      }
      return { ...acc, [direction]: material }
    }, {})
    return stateAcc
  }, {})

  directionalSpriteMaterialsCache.set(cacheKey, materials)
  return materials
}

const createDirectionalSpriteMarker = (
  feature,
  threeLayer,
  { namespace, featureType, initialState = "idle", scale = 1, spriteUrls }
) => {
  const { id, geometry, properties } = feature
  const coordinates = getCenterFromGeometry(geometry)
  const materials = getOriginMarkerDirectionalSpriteMaterials(spriteUrls)
  const initialDirection = "S"

  const marker = new SpriteMarker(
    coordinates,
    {
      scale,
      altitude: getAltitude(properties),
      anchor: "bottom",
      renderOrder: 5,
    },
    materials[initialState][initialDirection],
    threeLayer,
    {
      id,
      feature_type: featureType,
      namespace,
      ordinal: properties?.ordinal,
      directionalMaterials: materials,
      currentDirection: initialDirection,
      currentState: initialState,
      frameIndex: 0,
    }
  )

  threeLayer.addMesh([marker])
  return marker
}

/**
 * Retrieves the extrude configuration for a feature.
 *
 * @param {Object} baseExtrudeConfig - The default extrude configuration defined in the map
 *   settings of the app config content type.
 * @param {Object} feature - The feature for which the extrude configuration should be resolved.
 * @returns {Object|undefined} - The resolved extrude configuration, combining feature-specific
 *   extrude settings with the base configuration.
 */
export const getExtrudeConfigByFeature = (baseExtrudeConfig, feature = {}) => {
  const { feature_type: featureType } = feature
  // Retrieve extrude configuration from the extrude field in the feature's properties
  const featureExtrudeConfig = _.get(feature, "properties.extrude", {})
  // Retrieve extrude configuration from the base extrude settings. (Map Config)
  const featureCategory = _.get(feature, "properties.category")
  const baseFeatureExtrudeConfig = _.get(
    baseExtrudeConfig,
    `${featureType}.${featureCategory || featureType}`,
    {}
  )

  const mergedConfig = _.merge(
    {},
    baseFeatureExtrudeConfig,
    featureExtrudeConfig
  )
  // Fallback to base extrude configuration if the feature's extrude field is not defined.
  return _.isEmpty(mergedConfig) ? null : mergedConfig
}

const getFeatureMarkerConfig = (feature, mapConfig = {}) => {
  const { textMarkerType, extrudeConfig = {}, symbol } = mapConfig

  const location = getLocationByFeature(feature)
  const featureExtrudeConfig = getExtrudeConfigByFeature(
    extrudeConfig,
    location
  )

  return {
    markerSymbol: symbol,
    textMarkerType,
    featureExtrudeConfig,
  }
}

const markerSymbolToSvgParams = (symbol) => ({
  path: symbol?.markerPath,
  width: symbol?.markerPathWidth,
  height: symbol?.markerPathHeight,
  fill: symbol?.markerFill,
  stroke: symbol?.markerStroke,
  strokeWidth: symbol?.markerStrokeWidth,
})

const getMarkerScaleRatio = (innerSymbol, outerSymbol) => {
  const inner = innerSymbol?.markerWidth ?? innerSymbol?.markerPathWidth
  const outer = outerSymbol?.markerWidth ?? outerSymbol?.markerPathWidth
  return inner != null && outer ? inner / outer : undefined
}

const SECTION_LOGO_CANVAS_SIZE = 200

// Shadow bleed math, shared by every compositor that bakes ctx.shadow* into
// a canvas (unlike maptalks' native 2D-marker shadow).
const getShadowPadding = (symbol) => {
  const shadowOffsetX = symbol?.shadowOffsetX ?? 0
  const shadowOffsetY = symbol?.shadowOffsetY ?? 0
  const shadowBlur = symbol?.shadowBlur ?? 0
  const hasShadow =
    !!symbol?.shadowColor &&
    (shadowBlur > 0 || shadowOffsetX !== 0 || shadowOffsetY !== 0)
  const padding = hasShadow
    ? Math.ceil(shadowBlur + Math.abs(shadowOffsetX) + Math.abs(shadowOffsetY))
    : 0
  return {
    hasShadow,
    padding,
    shadowOffsetX,
    shadowOffsetY,
    shadowBlur,
    shadowColor: symbol?.shadowColor,
  }
}

// Adds canvas sizing + scale/anchor compensation for a given content box
// (fixed size for logos, per-tier for icons).
const getShadowMetrics = (symbol, contentW, contentH) => {
  const { padding, ...shadow } = getShadowPadding(symbol)
  const canvasW = contentW + padding * 2
  const canvasH = contentH + padding * 2
  return {
    ...shadow,
    padding,
    contentW,
    contentH,
    canvasW,
    canvasH,
    // Scales the enlarged canvas back down so the bubble's world size is
    // unchanged.
    scaleCompensation: { x: canvasW / contentW, y: canvasH / contentH },
    // Where the content's bottom edge sits in the padded canvas, so a
    // "bottom" anchor still hits the bubble tip.
    bottomAnchorY: padding / canvasH,
  }
}

// The section-logo composite is baked onto a fixed-size canvas texture.
const getSectionLogoShadowMetrics = (baseSymbol) => {
  const pathAspect =
    baseSymbol?.markerPathWidth && baseSymbol?.markerPathHeight
      ? baseSymbol.markerPathHeight / baseSymbol.markerPathWidth
      : 1
  const baseW = SECTION_LOGO_CANVAS_SIZE
  const baseH = Math.round(SECTION_LOGO_CANVAS_SIZE * pathAspect)
  return {
    pathAspect,
    baseW,
    baseH,
    ...getShadowMetrics(baseSymbol, baseW, baseH),
  }
}

// Shared box/normalization math: createSpriteMaterialByLabelSymbol bakes the
// texture at this size, create3DSectionIconMarker compensates against it.
const getIconCompositeShadowMetrics = (base, icon) => {
  const {
    markerWidth: baseWidth = 24,
    markerPathWidth: basePathW,
    markerPathHeight: basePathH,
  } = base ?? {}
  const { markerWidth: iconWidth = 24 } = icon ?? {}
  const viewBoxW = Math.max(baseWidth, iconWidth)
  const aspect = basePathW && basePathH ? basePathH / basePathW : 1
  const viewBoxH = Math.round(viewBoxW * aspect)
  // Normalize to the logo compositor's 200px-wide canonical box.
  const normW = 200
  const normH = Math.round(normW * (viewBoxH / viewBoxW))
  return {
    viewBoxW,
    viewBoxH,
    normW,
    normH,
    ...getShadowMetrics(base, normW, normH),
  }
}

export const createSectionLogoCompositeMaterial = (
  baseSymbol,
  logoUrl,
  { offsetX = 0, offsetY = 0, scale = 0.65 } = {}
) => {
  const {
    hasShadow,
    padding: SHADOW_PADDING,
    baseW: BASE_W,
    baseH: BASE_H,
    canvasW: CANVAS_W,
    canvasH: CANVAS_H,
  } = getSectionLogoShadowMetrics(baseSymbol)

  const canvas = document.createElement("canvas")
  canvas.width = CANVAS_W
  canvas.height = CANVAS_H
  const ctx = canvas.getContext("2d")
  const texture = new CanvasTexture(canvas)
  const material = new SpriteMaterial({
    color: 0xffffff,
    map: texture,
    // Alpha-cutout would clip a soft shadow's low alpha; use real blending
    // when a shadow is baked in.
    transparent: hasShadow,
    alphaTest: hasShadow ? 0.02 : 0.5,
    depthWrite: !hasShadow,
  })

  const SVG_W = baseSymbol?.markerWidth ?? BASE_W
  const SVG_H = Math.round(SVG_W * (BASE_H / BASE_W))
  const svg = buildPlainSvg({
    ...markerSymbolToSvgParams(baseSymbol),
    width: SVG_W,
    height: SVG_H,
  })

  const basePromise = svg
    ? loadSvgAsImage(svg)
        .then((baseImg) => {
          ctx.save()
          if (hasShadow) {
            ctx.shadowColor = baseSymbol.shadowColor
            ctx.shadowBlur = baseSymbol.shadowBlur ?? 0
            ctx.shadowOffsetX = baseSymbol.shadowOffsetX ?? 0
            ctx.shadowOffsetY = baseSymbol.shadowOffsetY ?? 0
          }
          ctx.drawImage(baseImg, SHADOW_PADDING, SHADOW_PADDING, BASE_W, BASE_H)
          ctx.restore()
          texture.needsUpdate = true
          material.needsUpdate = true
        })
        .catch((err) =>
          console.warn("Error painting base marker:", baseSymbol, err)
        )
    : Promise.resolve()

  if (logoUrl) {
    basePromise.then(() =>
      loadImage(logoUrl)
        .then((logoImg) => {
          ctx.save()
          ctx.translate(SHADOW_PADDING, SHADOW_PADDING)
          drawCenteredImage(ctx, logoImg, {
            size: Math.min(BASE_W, BASE_H),
            scale,
            offsetX,
            offsetY: offsetY - (BASE_H - BASE_W) / 2,
          })
          ctx.restore()
          texture.needsUpdate = true
        })
        .catch((err) => console.warn("Error loading logo image:", logoUrl, err))
    )
  }

  return material
}

export const createSpriteMaterialByLabelSymbol = (labelSymbol = []) => {
  const material = new SpriteMaterial()
  try {
    const [base, icon] = labelSymbol
    const { markerStrokeWidth: baseStrokeWidth = 0 } = base
    const pad = baseStrokeWidth

    // Shared with create3DSectionIconMarker's scale/anchor compensation.
    const {
      viewBoxW,
      viewBoxH,
      normW: NORM_W,
      normH: NORM_H,
      hasShadow,
      padding: SHADOW_PADDING,
      canvasW: CANVAS_W,
      canvasH: CANVAS_H,
    } = getIconCompositeShadowMetrics(base, icon)

    const canvas = document.createElement("canvas")
    canvas.width = CANVAS_W
    canvas.height = CANVAS_H
    const ctx = canvas.getContext("2d")
    const texture = new CanvasTexture(canvas)
    material.map = texture
    // Alpha-cutout would clip a soft shadow's low alpha; use real blending
    // when a shadow is baked in.
    material.transparent = hasShadow
    material.alphaTest = hasShadow ? 0.02 : 0.5
    material.depthWrite = !hasShadow

    // Separate SVG documents (not one combined string) so only the base
    // casts a shadow; same outer viewBox keeps them aligned.
    const svgViewBox = `${-pad} ${-pad} ${viewBoxW + 2 * pad} ${
      viewBoxH + 2 * pad
    }`
    const wrapSvg = (content) =>
      `<svg xmlns="http://www.w3.org/2000/svg" width="${NORM_W}" height="${NORM_H}" viewBox="${svgViewBox}">${content}</svg>`

    const baseSVG = wrapSvg(
      createSVGPathFromMarkerSymbol(base, {
        viewBoxWidth: viewBoxW,
        viewBoxHeight: viewBoxH,
      })
    )
    const iconSVG = wrapSvg(
      createSVGPathFromMarkerSymbol(icon, {
        viewBoxWidth: viewBoxW,
        viewBoxHeight: viewBoxW,
      })
    )

    loadSvgAsImage(baseSVG)
      .then((baseImg) => {
        ctx.save()
        if (hasShadow) {
          ctx.shadowColor = base.shadowColor
          ctx.shadowBlur = base.shadowBlur ?? 0
          ctx.shadowOffsetX = base.shadowOffsetX ?? 0
          ctx.shadowOffsetY = base.shadowOffsetY ?? 0
        }
        ctx.drawImage(baseImg, SHADOW_PADDING, SHADOW_PADDING, NORM_W, NORM_H)
        ctx.restore()
        texture.needsUpdate = true
        material.needsUpdate = true
        return loadSvgAsImage(iconSVG)
      })
      .then((iconImg) => {
        ctx.drawImage(iconImg, SHADOW_PADDING, SHADOW_PADDING, NORM_W, NORM_H)
        texture.needsUpdate = true
      })
      .catch((err) =>
        console.warn(
          `Error createSpriteMaterialByLabelSymbol: `,
          labelSymbol,
          err
        )
      )
  } catch (error) {
    console.warn(`Error createSpriteMaterialByLabelSymbol: `, labelSymbol)
  }
  return material
}

export const createStyledUIMarkerElement = ({
  style,
  textContent,
  className,
}) => {
  const element = document.createElement("div")
  for (const key in style) {
    element.style[key] = style[key]
  }
  element.className = className
  element.textContent = textContent
  //! Use outerHTML to return HTML string instead of element object to avoid DOM event warnings from Maptalks.js.
  return element.outerHTML
}

// A render type "involves a logo" when its name contains "Logo" ("Logo" or
// "Logo + Name"). Without a logo URL, fall back to the caller's non-logo type.
// requestedType may be null/undefined (e.g. render_type unset) — pass it through.
const validateLogoRenderType = (requestedType, logoUrl, fallback) =>
  requestedType?.includes("Logo") && !logoUrl ? fallback : requestedType

export const styledFeatureGenerator = (mapTheme) => {
  const getElementSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.geometry.symbol`)
    if (featureType === key) return featureTypeTheme
    const categoryTheme = _.get(mapTheme, `${key}.geometry.symbol`)
    return _.merge({}, featureTypeTheme, categoryTheme)
  }

  const getLabelSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.label`)
    const categoryTheme = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    let symbols = _.values(_.map(mergedSymbol, "symbol"))
    const markerIndexToMove = symbols.findIndex(
      ({ elementType }) => elementType === "label.marker"
    )

    // Move the label.marker symbol to the last of array
    if (markerIndexToMove >= 0) {
      const markerSymbolToMove = _.pullAt(symbols, markerIndexToMove)[0]
      symbols.push(markerSymbolToMove)
    }

    return symbols
  }

  const getUIMarkerSymbol = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.ui.marker`)
    const categoryTheme = _.get(mapTheme, `${key}.ui.marker`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    const symbol = _.get(mergedSymbol, "symbol")
    return symbol
  }
  const getUIMarkerOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeTheme = _.get(mapTheme, `${featureType}.ui.marker`)
    const categoryTheme = _.get(mapTheme, `${key}.ui.marker`)
    const mergedSymbol = _.merge({}, featureTypeTheme, categoryTheme)
    const options = _.get(mergedSymbol, "options")
    return options
  }

  const getLabelOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeSymbol = _.get(mapTheme, `${featureType}.label`)
    const categorySymbol = _.get(mapTheme, `${key}.label`)
    const mergedSymbol = _.merge({}, featureTypeSymbol, categorySymbol)
    return _.reduce(
      mergedSymbol,
      (acc, symbol) => ({ ...acc, ..._.get(symbol, "options", {}) }),
      {}
    )
  }

  const generateSpriteMarkerMaterial = () => {
    return SPRITE_MARKER_FEATURE.reduce((acc, featCate) => {
      const [featureType, category] = featCate.split("-")
      const key = `${featureType}.${category}`
      const labelSymbol = getLabelSymbol(key)
      const material = createSpriteMaterialByLabelSymbol(labelSymbol)
      return { ...acc, [key]: material }
    }, {})
  }

  const generateSpriteMarkerOption = () => {
    return SPRITE_MARKER_FEATURE.reduce((acc, featCate) => {
      const [featureType, category] = featCate.split("-")
      const key = `${featureType}.${category}`
      const options = getLabelOptions(key)
      return { ...acc, [key]: options }
    }, {})
  }

  const generateSpriteHighlightMarkerOption = () => {
    return SPRITE_HIGHLIGHT_MARKER_FEATURE.reduce((acc, featCate) => {
      const categoryKey = _.last(featCate.split("-")) // Example: highlighted-amenity.atm -> amenity.atm
      const defaultLabelSymbol = getLabelSymbol(categoryKey)
      const highlightLabelSymbol = getLabelSymbol(featCate)
      const [defaultBase, defaultIcon] = defaultLabelSymbol
      const [highlightBase, highlightIcon] = highlightLabelSymbol
      const base = _.merge({}, defaultBase, highlightBase)
      const icon = _.merge({}, defaultIcon, highlightIcon)

      const material = createSpriteMaterialByLabelSymbol([base, icon])
      const options = getLabelOptions(featCate)
      return { ...acc, [featCate]: { material, options } }
    }, {})
  }

  const spriteMarkerMaterialObj = generateSpriteMarkerMaterial()
  const spriteMarkerOptionObj = generateSpriteMarkerOption()
  const spriteHighlightMarkerOptionObj = generateSpriteHighlightMarkerOption()

  const sectionMaterialCache = {}

  const getSectionIconLabelSymbol = (
    category,
    priority,
    variant = "default",
    symbolKey = "icon"
  ) => {
    const baseKey =
      variant === "highlight"
        ? `highlight-section-${symbolKey}-${priority}`
        : `section-${symbolKey}-${priority}`
    const [baseSymbol, tierMarker] = getLabelSymbol(baseKey) ?? []
    const parentMarker = _.get(mapTheme, "section.label.marker.symbol")
    const categoryMarker = _.get(
      mapTheme,
      `section.${category}.label.marker.symbol`
    )
    const mergedMarker = _.merge({}, parentMarker, tierMarker, categoryMarker)
    return [baseSymbol, mergedMarker]
  }

  const getSectionLogoLabelSymbol = (
    priority,
    variant = "default",
    symbolKey = "logo"
  ) => {
    const key =
      variant === "highlight"
        ? `highlight-section-${symbolKey}-${priority}`
        : `section-${symbolKey}-${priority}`
    return getLabelSymbol(key) ?? []
  }

  const getSectionMaterial = (
    category,
    priority,
    variant = "default",
    symbolKey = "icon"
  ) => {
    const cacheKey = `${variant}.section.${symbolKey}.${category}.${priority}`
    if (!sectionMaterialCache[cacheKey]) {
      const labelSymbol = getSectionIconLabelSymbol(
        category,
        priority,
        variant,
        symbolKey
      )
      sectionMaterialCache[cacheKey] =
        createSpriteMaterialByLabelSymbol(labelSymbol)
    }
    return sectionMaterialCache[cacheKey]
  }

  const getElementOptions = (key) => {
    const [featureType] = key.split(".")
    const featureTypeOptions = _.get(
      mapTheme,
      `${featureType}.geometry.options`
    )

    const categoryOptions = _.get(mapTheme, `${key}.geometry.options`)

    return _.merge({}, featureTypeOptions, categoryOptions)
  }

  const createOccupantMarker = (feature, locatedFeature, mapConfig) => {
    const { textMarkerType, featureExtrudeConfig } = getFeatureMarkerConfig(
      feature,
      mapConfig
    )
    const markerHeight = _.get(featureExtrudeConfig, "height", 0)
    const { properties, id: featureId, feature_type } = feature
    if (!properties.anchor) return

    /**
     *  Prepare geometry for occupant marker positioning:
     *  - Use anchor coordinates if the location feature is the main location
     *  - Otherwise, use the center of the location feature
     */
    const locationFeatureId = locatedFeature?.id
    const mainLocationId =
      _.get(properties, "unit.id") || _.get(properties, "kiosk.id")
    const isMainLocationFeature = mainLocationId === locationFeatureId
    const { geometry: mainLocationGeometry } = properties?.anchor
    const geometry = isMainLocationFeature
      ? mainLocationGeometry
      : turfCenter(locatedFeature)?.geometry

    const id = isMainLocationFeature
      ? featureId
      : buildCompositeId([featureId, locationFeatureId], {
          suffix: SUFFIX_MARKER,
        })
    const baseProperties = {
      id,
      feature_type,
      category: properties.category,
      altitude: getAltitude(properties),
    }
    const occupantName = _.isEmpty(properties.short_name)
      ? properties.name
      : properties.short_name

    if (locatedFeature) {
      const { feature_type, properties } = locatedFeature
      const { category } = properties
      const locatedLabelOption =
        feature_type === "kiosk"
          ? getLabelOptions(`${feature_type}`)
          : getLabelOptions(`${feature_type}.${category}`)
      const { hidden = false } = locatedLabelOption || {}
      if (hidden) return
    }

    const logoUrl = _.get(properties, "logo.url")
    const requestedRenderType = _.get(properties, "render_type", "Name")
    const renderType = validateLogoRenderType(
      requestedRenderType,
      logoUrl,
      "Name"
    )
    if (renderType === "Label") return null

    const renderPriority = properties.render_priority || 3
    const labelSymbol = getLabelSymbol(`occupant-${_.toLower(renderType)}`)
    const markerSymbol = _.last(labelSymbol)

    const coordinates = _.get(geometry, "coordinates")

    const priorityLabelSymbol = getLabelSymbol(
      `occupant-${_.toLower(renderType)}-${renderPriority}`
    )
    const priorityMarkerSymbol = _.last(priorityLabelSymbol) || {}

    switch (renderType) {
      case "Logo":
        _.set(priorityMarkerSymbol, "markerFile", logoUrl)

        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
            ...baseProperties,
          },
          symbol: priorityLabelSymbol,
        })

      case "Logo + Name":
        return new Marker(coordinates, {
          properties: {
            name: occupantName.en,
            altitude: getAltitude(properties) + markerHeight,
            ...baseProperties,
          },
          symbol: {
            textName: "{name}",
            markerFile: logoUrl,
            textHaloRadius: {
              stops: [
                [20, 1],
                [21, 2],
              ],
            },
            ...markerSymbol,
            ...priorityMarkerSymbol,
          },
        })
      case "None":
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style: { display: "none" },
            textContent: occupantName.en,
          }),
          altitude: getAltitude(properties) + markerHeight,
        })
      case "Name":
      default:
        if (textMarkerType === "marker") {
          return new Marker(coordinates, {
            properties: {
              name: occupantName.en,
              altitude: getAltitude(properties) + markerHeight,
              ...baseProperties,
            },
            symbol: {
              textName: "{name}",
              //TODO:: implement symbol scaler later
              textHaloRadius: {
                stops: [
                  [20, 1],
                  [21, 2],
                ],
              },
              ...markerSymbol,
              ...priorityMarkerSymbol,
            },
          })
        }

        const uiMarkerSymbol = getUIMarkerSymbol(
          `occupant-${_.toLower(renderType)}`
        )
        const uiMarkerPrioritySymbol = getUIMarkerSymbol(
          `occupant-${_.toLower(renderType)}-${renderPriority}`
        )
        const uiMarkerPriorityOptions = getUIMarkerOptions(
          `occupant-${_.toLower(renderType)}-${renderPriority}`
        )

        const style = { ...uiMarkerSymbol, ...uiMarkerPrioritySymbol }
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style,
            textContent: occupantName.en,
            className: OCCUPANT_TEXT_MARKER_CLASSNAME,
          }),
          collision: true,
          collisionFadeIn: true,
          altitude: getAltitude(properties) + markerHeight,
          ...uiMarkerPriorityOptions,
        })
    }
  }

  const createSectionMarker = (feature, locatedFeature, mapConfig) => {
    const { textMarkerType, featureExtrudeConfig } = getFeatureMarkerConfig(
      feature,
      mapConfig
    )
    const markerHeight = _.get(featureExtrudeConfig, "height", 0)
    const { properties, id, feature_type } = feature

    const displayPoint = _.get(feature, "properties.display_point")
    const coordinates = getCenterFromGeometry(displayPoint || feature.geometry)

    if (!coordinates) return

    const baseProperties = {
      id: buildCompositeId([id], {
        suffix: SUFFIX_MARKER,
      }),
      feature_type,
      category: properties.category,
      altitude: getAltitude(properties) + markerHeight,
    }
    const sectionName = _.isEmpty(properties.short_name)
      ? properties.name
      : properties.short_name

    if (locatedFeature) {
      const { feature_type, properties } = locatedFeature
      const { category } = properties
      const locatedLabelOption =
        feature_type === "kiosk"
          ? getLabelOptions(`${feature_type}`)
          : getLabelOptions(`${feature_type}.${category}`)
      const { hidden = false } = locatedLabelOption || {}
      if (hidden) return
    }

    const logoUrl = _.get(properties, "logo.url")
    const requestedRenderType = _.get(properties, "render_type", "Name")
    const renderType = validateLogoRenderType(
      requestedRenderType,
      logoUrl,
      "Name"
    )

    if (renderType === "Label") return null

    const renderPriority = properties.render_priority || 3
    const symbolKey = getSectionMarkerSymbolKey(renderType)
    const labelSymbol = getLabelSymbol(`section-${symbolKey}`)
    const markerSymbol = _.last(labelSymbol)

    const priorityLabelSymbol = getLabelSymbol(
      `section-${symbolKey}-${renderPriority}`
    )

    const priorityMarkerSymbol = _.last(priorityLabelSymbol) || {}

    switch (renderType) {
      case "Logo":
        _.set(priorityMarkerSymbol, "markerFile", logoUrl)

        return new Marker(coordinates, {
          properties: {
            ...baseProperties,
          },
          symbol: priorityLabelSymbol,
        })

      case "Logo + Name":
        return new Marker(coordinates, {
          properties: {
            name: sectionName.en,
            ...baseProperties,
          },
          symbol: {
            textName: "{name}",
            markerFile: logoUrl,
            textHaloRadius: {
              stops: [
                [20, 1],
                [21, 2],
              ],
            },
            ...markerSymbol,
            ...priorityMarkerSymbol,
          },
        })
      case "None":
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style: { display: "none" },
            textContent: sectionName.en,
          }),
          altitude: getAltitude(properties) + markerHeight,
        })
      case "Name":
      default:
        if (textMarkerType === "marker") {
          return new Marker(coordinates, {
            properties: {
              name: sectionName.en,
              ...baseProperties,
            },
            symbol: {
              textName: "{name}",
              //TODO:: implement symbol scaler later
              textHaloRadius: {
                stops: [
                  [20, 1],
                  [21, 2],
                ],
              },
              ...markerSymbol,
              ...priorityMarkerSymbol,
            },
          })
        }

        const uiMarkerSymbol = getUIMarkerSymbol(`section-${symbolKey}`)
        const uiMarkerPrioritySymbol = getUIMarkerSymbol(
          `section-${symbolKey}-${renderPriority}`
        )
        const uiMarkerPriorityOptions = getUIMarkerOptions(
          `section-${symbolKey}-${renderPriority}`
        )

        const style = { ...uiMarkerSymbol, ...uiMarkerPrioritySymbol }
        return new ui.UIMarker(coordinates, {
          properties: { ...baseProperties },
          content: createStyledUIMarkerElement({
            style,
            textContent: sectionName.en,
            className: SECTION_TEXT_MARKER_CLASSNAME,
          }),
          collision: true,
          collisionFadeIn: true,
          altitude: getAltitude(properties) + markerHeight,
          ...uiMarkerPriorityOptions,
        })
    }
  }

  const getFeatureProperties = (feature) => {
    const { id, feature_type, properties, geometry } = feature
    const { category, ordinal, style = {} } = properties
    const elementStyle = getElementSymbol(`${feature_type}.${category}`)
    const {
      allowOverride,
      topOutline = false,
      bottomOutline = false,
      visible = true,
    } = getElementOptions(`${feature_type}.${category}`)
    if (allowOverride) _.merge(elementStyle, style)
    const {
      polygonFill,
      lineColor,
      lineWidth: width,
      polygonOpacity,
    } = elementStyle
    const color = geometry?.type === "LineString" ? lineColor : polygonFill
    const featureProperty = {
      id,
      feature_type,
      category: category || feature_type,
      ordinal,
      defaultColor: color,
      defaultLineWidth: width,
      topOutline,
      bottomOutline,
      defaultOutlineColor: lineColor,
      opacity: polygonOpacity,
      visible,
    }
    return featureProperty
  }

  const styler = {
    getElementSymbol,
    getHilighPolygonalSymbol: (type) => {
      switch (type) {
        case "MultiPolygon":
        case "Polygon": {
          return getElementSymbol("hilight-polygonal")
        }
        case "Point": {
          return getElementSymbol("hilight-marker")
        }
        default:
          break
      }
    },
    getHighlightMarkerSymbol: () => {
      return getElementSymbol("highlighted-marker")
    },
    createVenue: (feature) => {
      const { geometry, feature_type } = feature
      const symbolStyle = getElementSymbol(feature_type)
      return new GeometryType[geometry.type](geometry.coordinates, {
        symbol: {
          ...symbolStyle,
        },
        zIndex: VENUE_Z_INDEX,
      })
    },

    createLevel: (feature) => {
      const { geometry, properties, feature_type } = feature
      const { category } = properties

      const style = getElementSymbol(`${feature_type}.${category}`)
      // const options = getElementOptions(`${feature_type}.${category}`)

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          altitude: getAltitude(properties),
        },
        symbol: {
          ...style,
        },
        zIndex: LEVEL_Z_INDEX,
      })
    },

    createUnit: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(`${feature_type}.${category}`)
      const zIndex = UNIT_Z_INDEX
      switch (feature.properties.category) {
        case "room":
        case "terrace":
        case "unenclosedarea":
        case "vegetation":
        case "unspecified":
        case "recreation":
          return createRoomUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "firstaid":
        case "restroom":
        case "restroom.female":
        case "restroom.male":
        case "escalator":
        case "elevator":
        case "stairs":
        case "stairs.emergencyexit":
        case "parking":
        case "smokingarea":
        case "mothersroom":
        case "privatelounge":
        case "shuttle":
        case "fieldofplay":
          return createAmenityUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "nonpublic":
        case "structure":
        case "brick":
        case "concrete":
        case "drywall":
        case "glass":
        case "wood":
        case "column":
        case "opentobelow":
          return createNonpublicUnit(feature, elementStyle, options).setZIndex(
            zIndex
          )
        case "walkway":
        case "footbridge":
        case "ramp":
          return createWalkwayUnit(feature, elementStyle, options).setZIndex(
            zIndex - 1
          )
        default:
          break
      }
    },

    createMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const markerSymbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          symbol: markerSymbol,
        })
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },
    createOriginMarker: (feature) => {
      const { id, geometry, properties } = feature
      const coordinates = getCenterFromGeometry(geometry)
      const markerSymbol = getElementSymbol("origin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
          },
          id,
          symbol: markerSymbol,
        })
      } catch (error) {
        console.log(`error creating origin marker:`, feature)
      }
    },
    createDirectionalOriginMarkerSprite: (feature, threeLayer, options = {}) =>
      createDirectionalSpriteMarker(feature, threeLayer, {
        namespace: "origin-marker",
        featureType: "origin-marker",
        scale: options.scale,
        spriteUrls: options.spriteUrls,
      }),
    createDirectionalUserLocationMarkerSprite: (
      feature,
      threeLayer,
      options = {}
    ) =>
      createDirectionalSpriteMarker(feature, threeLayer, {
        namespace: "user-location",
        featureType: feature.feature_type,
        scale: options.scale,
        spriteUrls: options.spriteUrls,
      }),
    createDestinationPinMarker: (feature, mapConfig) => {
      const { featureExtrudeConfig } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height")
      // Get Label from located unit / kiosk
      const { id, properties } = feature
      const geometry =
        _.get(feature, "geometry") ||
        _.get(feature, "properties.anchor.geometry")
      const coordinates = getCenterFromGeometry(geometry)
      const symbol = getElementSymbol("pin-marker")
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
          },
          id,
          symbol,
        })
      } catch (error) {
        console.log(`error creating destination marker:`, feature)
      }
    },
    createDestinationLogoMarker: (feature, mapConfig = {}) => {
      const { featureExtrudeConfig } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height", 0)

      // Get Label from located unit / kiosk
      const { properties, id } = feature
      const { geometry } = properties.anchor
      const logoUrl = _.get(properties, "logo.url")
      const coordinates = getCenterFromGeometry(geometry)
      const [markerBase, markerLabel] = getLabelSymbol(
        "highlighted-logo-marker"
      )
      try {
        return new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties) + markerHeight,
          },
          id,
          symbol: [markerBase, { ...markerLabel, markerFile: logoUrl }],
        })
      } catch (error) {
        console.log(`error creating destination marker:`, feature)
      }
    },

    createUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle.start,
          zIndex: USER_LOCATION_Z_INDEX,
        })

        marker.animate(
          {
            symbol: symbolStyle.end,
          },
          {
            repeat: true,
            easing: "upAndDown",
            duration: 1500,
          }
        )
        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createUserLocationUIMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const uiMarkerSymbolStyle = getUIMarkerSymbol("user-location") || {}
        const { locale_symbol, ...rest } = uiMarkerSymbolStyle
        const marker = new ui.UIMarker(coordinates, {
          properties: {
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
            symbol: uiMarkerSymbolStyle,
            locale_symbol,
          },
          content: createStyledUIMarkerElement({
            style: { ...rest },
            textContent: null,
            className: "user-location",
          }),
          altitude: getAltitude(properties),
        })
        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createLastUserLocationMarker: (feature) => {
      try {
        const { geometry, properties } = feature
        const coordinates = getCenterFromGeometry(geometry)
        const symbolStyle = getElementSymbol("last-user-location") || {}
        const options = getElementOptions("last-user-location") || {}
        const marker = new Marker(coordinates, {
          properties: {
            ...options,
            altitude: getAltitude(properties),
            ordinal: properties.ordinal !== null ? properties.ordinal : null,
          },
          symbol: symbolStyle,
          zIndex: LAST_LOCATION_Z_INDEX,
        })

        return marker
      } catch (err) {
        console.log(`error creating marker:`, feature)
      }
    },

    createHighlightOccupantMarker: (feature, mapConfig) => {
      const { featureExtrudeConfig, markerSymbol } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )
      const markerHeight = _.get(featureExtrudeConfig, "height")
      const { id, feature_type, properties } = feature

      if (!properties.anchor) return

      const markerProperties = {
        ...properties,
        id,
        feature_type,
        altitude: getAltitude(properties) + markerHeight,
      }

      const { geometry } = properties?.anchor
      const coordinates = _.get(geometry, "coordinates")
      const logoUrl = _.get(properties, "logo.url")

      // If the symbol option is defined, create a marker.
      if (markerSymbol) {
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol: markerSymbol,
        })
      }

      //use pin-marker if no logo
      if (!logoUrl) {
        const symbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol,
        })
      }

      const labelSymbol = getLabelSymbol("highlight-occupant-logo")
      const priorityMarkerSymbol = _.last(labelSymbol) || {}
      _.set(priorityMarkerSymbol, "markerFile", logoUrl)
      return new Marker(coordinates, {
        properties: markerProperties,
        symbol: labelSymbol,
      })
    },
    createHighlightSectionMarker: (feature, mapConfig) => {
      const { featureExtrudeConfig, markerSymbol } = getFeatureMarkerConfig(
        feature,
        mapConfig
      )

      const markerHeight = _.get(featureExtrudeConfig, "height", 0)
      const { extrudeConfig } = mapConfig ?? {}
      const sectionExtrudeConfig = getExtrudeConfigByFeature(
        extrudeConfig,
        feature
      )
      const sectionHeight = sectionExtrudeConfig?.height ?? 0
      const { id, feature_type, properties } = feature

      const displayPoint = _.get(feature, "properties.display_point")
      const coordinates = getCenterFromGeometry(
        displayPoint || feature.geometry
      )
      if (!coordinates) return

      const markerProperties = {
        ...properties,
        id,
        feature_type,
        altitude: getAltitude(properties) + markerHeight + sectionHeight,
      }

      const logoUrl = _.get(properties, "logo.url")

      // If the symbol option is defined, create a marker.
      if (markerSymbol) {
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol: markerSymbol,
        })
      }

      //use pin-marker if no logo
      if (!logoUrl) {
        const symbol = getElementSymbol("pin-marker")
        return new Marker(coordinates, {
          properties: markerProperties,
          symbol,
        })
      }

      const renderPriority = properties.render_priority || 3
      const renderType = _.get(properties, "render_type", "Name")
      const themeKey =
        renderType === "Logo"
          ? `highlight-section-logo-${renderPriority}`
          : `highlight-section-logo`
      const labelSymbol = getLabelSymbol(themeKey)
      const priorityMarkerSymbol = _.last(labelSymbol) || {}
      _.set(priorityMarkerSymbol, "markerFile", logoUrl)
      return new Marker(coordinates, {
        properties: markerProperties,
        symbol: labelSymbol,
      })
    },
    createHighlight2DAmenityMarkerFrom3DMarker: (feature, mapConfig) => {
      const { coordinates, units, kiosk } = feature
      const amenityLocatedUnit = _.first(units)

      const unitConfig = getExtrudeConfigByFeature(
        mapConfig,
        amenityLocatedUnit
      )
      const unitHeight = _.get(unitConfig, "height", 0)
      const kioskConfig = getExtrudeConfigByFeature(mapConfig, kiosk)
      const kioskHeight = _.get(kioskConfig, "height", 0)

      const markerHeight = unitHeight + kioskHeight

      const symbol = getElementSymbol("highlight-amenity-marker")
      return new Marker(coordinates, {
        properties: {
          ...feature,
          altitude: markerHeight,
        },
        symbol,
      })
    },
    createKiosk: (feature) => {
      const { geometry, feature_type, properties, id } = feature
      const {
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = getElementOptions(
        `${feature_type}.${category}.geometry.options`
      )

      const { allowOverride, inheritFillColorToLine } = options

      const symbolStyle = { ...elementStyle }

      if (allowOverride) _.merge(symbolStyle, properties.style)
      if (inheritFillColorToLine)
        symbolStyle.lineColor = symbolStyle.polygonFill

      return new GeometryType[geometry.type](geometry.coordinates, {
        properties: {
          id,
          feature_type,
          category: "kiosk",
          altitude: getAltitude(properties),
        },
        symbol: symbolStyle,
        defaultSymbol: symbolStyle,
        zIndex: KIOSK_Z_INDEX,
      })
    },

    createSection: (feature) => {
      const { properties, feature_type } = feature
      const { category } = properties
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: SECTION_Z_INDEX,
      }

      switch (category) {
        case "eatingdrinking":
          return createEatingDrinkingSection(feature, elementStyle, options)
        case "vegetation":
          return createVegetationSection(feature, elementStyle, options)
        case "seating":
          return creatingSeatingSection(feature, elementStyle, options)
        default:
          return creatingDefaultSection(feature, elementStyle, options)
      }
    },
    createSectionMarker,
    createOccupant: (feature, location, mapConfig) => {
      return createOccupantMarker(feature, location, mapConfig)
    },

    createOpening: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: OPENING_Z_INDEX,
      }
      switch (feature.properties.category) {
        case "pedestrian":
          return createPedestrianOpening(feature, elementStyle, options)
        case "pedestrian.principal":
          return createPrincipalOpening(feature, elementStyle, options)
        case "emergencyexit":
          return createEmergencyExitOpening(feature, elementStyle, options)
        default:
          break
      }
    },

    createFixture: (feature) => {
      const {
        feature_type,
        properties: { category },
      } = feature
      const elementStyle = getElementSymbol(`${feature_type}.${category}`)
      const options = {
        ...getElementOptions(`${feature_type}.${category}`),
        zIndex: FIXTURE_Z_INDEX,
      }
      switch (feature.properties.category) {
        case "water":
          return createWaterFixture(feature, elementStyle, options)
        case "vegetation":
          return createVegetationFixture(feature, elementStyle, options)
        case "equipment":
        case "obstruction":
        case "stage":
        case "desk":
        case "wall":
          return createObstructionalFixture(feature, elementStyle, options)
        default:
          break
      }
    },

    createLineStringFromGeometries: (geometries) => {
      const mergedCoordinates = _(geometries)
        .map((geometry) => {
          switch (geometry.type) {
            case "Point":
              return [
                geometry?.getCoordinates
                  ? geometry?.getCoordinates()
                  : new Coordinate(geometry.coordinates),
              ]
            case "Polygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new Polygon(geometry.coordinates).getCenter(),
              ]
            case "MultiPolygon":
              return [
                geometry?.getCenter
                  ? geometry?.getCenter()
                  : new MultiPolygon(geometry.coordinates).getCenter(),
              ]
            case "LineString":
            default:
              return geometry?.getCoordinates
                ? geometry?.getCoordinates()
                : new LineString(geometry.coordinates).getCoordinates()
          }
        })
        .flatten()
        .value()
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const startPathSymbolMarker = getElementSymbol("navigation-path-start")

      const line = new LineString(mergedCoordinates, {
        smoothness: 0.5,
        symbol: [...stepPathLineSymbol, startPathSymbolMarker],
      })

      return line
    },
    create3DStepPath: (feature, threeLayer, option = {}) => {
      const stepPathLineSymbol = getElementSymbol("navigation-path")
      const pathLineSymbol = stepPathLineSymbol[1]
      const pathLineEffect = stepPathLineSymbol[0]
      const lineOptions = {
        color: pathLineSymbol?.lineColor,
        opacity: pathLineSymbol?.lineOpacity,
      }
      const outlineOptions = {
        color: pathLineEffect?.lineColor,
        opacity: pathLineEffect?.lineOpacity,
      }

      return new NavigationPath(
        feature,
        threeLayer,
        getFeatureProperties(feature),
        option,
        lineOptions,
        outlineOptions
      )
    },

    createDecoration: (decoration, options) => {
      const { type, coordinates } = decoration
      const { id, ordinal, symbol } = options
      const formattedProperties = {
        properties: {
          id,
          altitude: getAltitude(options),
          ordinal,
        },
        symbol,
        zIndex: DECORATION_Z_INDEX,
      }
      switch (type) {
        case "Polygon":
          return new Polygon(coordinates, formattedProperties)
        case "MultiPolygon":
          return new MultiPolygon(coordinates, formattedProperties)
        case "LineString":
          return new LineString(coordinates, formattedProperties)
        case "MultiLineString":
          return new MultiLineString(coordinates, formattedProperties)
        default:
          return null
      }
    },

    /** Three JS */
    create3DFootprint: async (feature, threeLayer, options) => {
      const objects = []
      const { height } = options || {}
      if (!height) return objects

      const { properties } = feature
      const footprintProperties = getFeatureProperties(feature)

      // Use properties.model3d if exists
      const hasModel3ds =
        Array.isArray(properties.model3d) && properties.model3d.length > 0
      if (hasModel3ds) {
        const models = properties.model3d
        const center = turfCenter(feature)
        const coordinate = _.get(center, "geometry.coordinates")
        for (const model of models) {
          const object = await loadModel3d(model, coordinate, threeLayer)
          object.properties = footprintProperties
          objects.push(object)
        }
      } else {
        const color = footprintProperties.defaultColor
        //skip create extrude polygon if color === "transparent" because js can't convert color:"transparent" to material color
        if (color === "transparent") return
        const material = new MeshLambertMaterial({
          color,
          transparent: true,
        })

        const meshs = createExtrudePolygon(
          feature.geometry,
          threeLayer,
          material,
          height,
          footprintProperties,
          {}
        )
        objects.push(...meshs)
      }

      return objects
    },
    create3DGroundLabel: (label, threeLayer, altitude = 0) => {
      const text = label.properties.name
      const bound = label.geometry.coordinates
      if (_.isNil(bound)) throw new Error("Invalid coordinates")

      // Retrieve label symbol from map theme
      const groundLabelSymbol = getElementSymbol("ground-label")

      // Retrieve label symbol from label feature properties
      const featureSymbol = _.get(label, "properties", {})

      // Apply default style from map theme if not defined in label feature
      const groundLabelOptions = _.merge(
        {},
        { text },
        groundLabelSymbol,
        featureSymbol,
        { altitude: altitude + 0.05, fitToPlane: true }
      )

      return new GroundLabel(bound, groundLabelOptions, threeLayer)
    },

    createOccupantGroundLabel: (feature, location, mapConfig, threeLayer) => {
      const text =
        feature.properties.short_name?.en || feature.properties.name.en
      const bound = location.geometry.coordinates
      if (_.isNil(bound)) throw new Error("Invalid coordinates")

      const groundLabelSymbol = getElementSymbol("occupant-flat-label")
      const groundLabelOptions = getElementOptions("occupant-flat-label")

      const baseAltitude = getAltitude(location.properties)
      const { extrudeConfig } = mapConfig
      const featureExtrudeConfig = getExtrudeConfigByFeature(
        extrudeConfig,
        location
      )
      const extrudeHeight = _.get(featureExtrudeConfig, "height", 0)
      const totalAltitude = baseAltitude + extrudeHeight + 0.05

      const featureSymbol = {
        name: text,
        ordinal: location.properties.ordinal,
        venue_id: feature.properties.venue_id,
      }

      const {
        angle,
        offsetX = 0,
        offsetY = 0,
        fontSize,
        minLabelFontSize,
        maxLabelFontSize,
      } = _.get(feature, "properties.style") ?? {}

      const customStyle = _.omitBy(
        { angle, fontSize, minLabelFontSize, maxLabelFontSize },
        _.isNil
      )

      const mergedGroundLabelOptions = _.merge(
        {},
        { text },
        groundLabelSymbol,
        groundLabelOptions,
        featureSymbol,
        {
          altitude: totalAltitude,
          offsetX,
          offsetY,
          ...customStyle,
        }
      )

      const groundLabel = new GroundLabel(
        bound,
        mergedGroundLabelOptions,
        threeLayer
      )
      return groundLabel
    },
    create3DBillboard,
    create3DAmenityMarker,
    renderAmenityMarker: (feature, threeLayer, config) => {
      // TODO: Integrate render_type "Logo" to replace is_feature.
      const { is_featured, render_type } = feature?.properties ?? {}
      const renderType = is_featured ? "Logo" : render_type ?? "Icon"
      switch (renderType) {
        case "Logo":
          return create3DBillboard(feature, threeLayer)
        case "None":
          return
        case "Icon":
        default:
          return create3DAmenityMarker(feature, threeLayer, {
            ...(config ?? {}),
            spriteMarkerMaterialObj,
            spriteHighlightMarkerOptionObj,
            spriteMarkerOptionObj,
          })
      }
    },

    renderSectionMarker: (feature, threeLayer, config) => {
      const { properties } = feature
      const { render_type } = properties

      const logoUrl = _.get(properties, "logo.url")

      const renderType = validateLogoRenderType(render_type, logoUrl, "Icon")

      switch (renderType) {
        case "Logo": {
          const renderPriority = properties.render_priority || 3
          const symbolKey = getSectionMarkerSymbolKey(render_type)
          const [baseSymbol, logoSymbol] = getSectionLogoLabelSymbol(
            renderPriority,
            "default",
            symbolKey
          )
          const [highlightBaseSymbol, highlightLogoSymbol] =
            getSectionLogoLabelSymbol(renderPriority, "highlight", symbolKey)
          const marker = create3DSectionLogoMarker(feature, threeLayer, {
            ...(config ?? {}),
            logoUrl,
            spriteMarkerOptionObj,
            baseSymbol,
            logoSymbol,
            highlightBaseSymbol,
            highlightLogoSymbol,
          })
          return [marker]
        }
        case "None":
        case "Icon":
        default: {
          const renderPriority = properties.render_priority || 3
          // Only "Icon"/"Logo" (and zone variants) have section-<key>-<tier>
          // theme tiers. "None" is a visibility instruction, not a tier — it
          // still needs a real bubble for the highlight state (SpriteMarker
          // force-shows it), and "Label" has no tier either. Both borrow the
          // icon tier.
          const symbolKey = getSectionMarkerSymbolKey(
            render_type === "Icon" || render_type === "Logo"
              ? render_type
              : "Icon"
          )
          const { category } = properties
          // Same merged (section + category override) source
          // getSectionMaterial bakes its texture from, so compensation
          // matches what's drawn.
          const [baseSymbol, iconSymbol] = getSectionIconLabelSymbol(
            category,
            renderPriority,
            "default",
            symbolKey
          )
          const [highlightBaseSymbol, highlightIconSymbol] =
            getSectionIconLabelSymbol(
              category,
              renderPriority,
              "highlight",
              symbolKey
            )
          const iconMarker = create3DSectionIconMarker(feature, threeLayer, {
            ...(config ?? {}),
            material: getSectionMaterial(
              category,
              renderPriority,
              "default",
              symbolKey
            ),
            highlightMaterial: getSectionMaterial(
              category,
              renderPriority,
              "highlight",
              symbolKey
            ),
            spriteMarkerMaterialObj,
            spriteHighlightMarkerOptionObj,
            spriteMarkerOptionObj,
            baseSymbol,
            iconSymbol,
            highlightBaseSymbol,
            highlightIconSymbol,
            visible: render_type !== "None",
          })

          return [iconMarker]
        }
      }
    },
    create3DOccupantAmenityMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, unit, kiosk } = properties
      const amenityLocation = kiosk || unit
      const locationConfig = getExtrudeConfigByFeature(config, amenityLocation)
      const coordinates = _.get(geometry, "coordinates")

      const markerProperties = {
        ...properties,
        coordinates,
        id,
        feature_type,
      }

      const material = _.get(
        spriteMarkerMaterialObj,
        `${feature_type}.${category}`
      )

      const markerOptions = _.get(
        spriteMarkerOptionObj,
        `${feature_type}.${category}`
      )

      const highlightOptions = _.get(
        spriteHighlightMarkerOptionObj,
        `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
      )

      const options = {
        scale: 0.05,
        altitude: _.get(locationConfig, "height", 0),
        highlight: highlightOptions,
        renderOrder: 5,
        anchor: markerOptions?.markerAnchor,
        offsetAltitude: markerOptions?.offsetAltitude,
      }

      return create3DMarker(
        coordinates,
        options,
        material,
        threeLayer,
        markerProperties
      )
    },
    create3DOpeningMarker: (feature, threeLayer, config) => {
      const { geometry, properties, feature_type, id } = feature
      const { category, unit, kiosk } = properties
      const amenityLocation = kiosk || unit
      const locationConfig = getExtrudeConfigByFeature(config, amenityLocation)
      const coordinates = _.get(geometry, "coordinates")

      const markerProperties = {
        ...properties,
        coordinates,
        id,
        feature_type,
      }

      const material = _.get(
        spriteMarkerMaterialObj,
        `${feature_type}.${category}`
      )

      const markerOptions = _.get(
        spriteMarkerOptionObj,
        `${feature_type}.${category}`
      )

      const highlightOptions = _.get(
        spriteHighlightMarkerOptionObj,
        `${PREFIX_HIGHLIGHTED_SYMBOL_KEY}-${feature_type}.${category}`
      )

      const options = {
        scale: 0.05,
        altitude: _.get(locationConfig, "height", 0),
        highlight: highlightOptions,
        renderOrder: 5,
        anchor: markerOptions?.markerAnchor,
        offsetAltitude: markerOptions?.offsetAltitude,
      }

      return create3DMarker(
        coordinates,
        options,
        material,
        threeLayer,
        markerProperties
      )
    },
    create3DUnit: async (unit, threeLayer) => {
      const { id, feature_type, properties } = unit
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(unit)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },

    create3DKiosk: async (kiosk, threeLayer) => {
      const { id, feature_type, properties } = kiosk
      const { ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        ordinal,
      }

      const center = turfCenter(kiosk)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },

    create3DFixture: async (fixture, threeLayer) => {
      const { id, feature_type, properties } = fixture
      const { category, ordinal, model3d } = properties

      const modelProperty = {
        id,
        feature_type,
        category,
        ordinal,
      }

      const center = turfCenter(fixture)
      const coordinate = _.get(center, "geometry.coordinates")
      const models = await create3DModels(
        model3d,
        coordinate,
        modelProperty,
        threeLayer
      )
      return models
    },
    createExtrudedUnit: (feature, threeLayer, options) => {
      const { height, altitude, bottomHeight, offset } = options || {}

      if (!height) return

      //NOTE: this is temporary condition that only extrude when the unit is "room"
      const unitProperty = getFeatureProperties(feature)

      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        // TODO: Move to extrude config later
        offset: offset ?? -0.1,
        altitude: altitude ?? 0,
        bottomHeight: bottomHeight ?? 0,
        topOutline: unitProperty.topOutline,
        bottomOutline: unitProperty.bottomOutline,
        outlineColor: unitProperty.defaultOutlineColor,
        linewidth: unitProperty.defaultLineWidth,
      }

      const color = unitProperty.defaultColor
      if (color === "transparent") return
      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })

      if (feature.geometry.type === "LineString") {
        const line = lineString(feature.geometry.coordinates)
        const cleanedLine = cleanCoords(line)
        return [
          createExtrudeLine(
            cleanedLine.geometry,
            threeLayer,
            material,
            height,
            unitProperty,
            options3d
          ),
        ]
      }

      const meshs = createExtrudePolygon(
        feature.geometry,
        threeLayer,
        material,
        height,
        unitProperty,
        options3d
      )

      return meshs
    },
    createExtrudedKiosk: (kiosk, threeLayer, options) => {
      const { height, altitude, bottomHeight } = options || {}

      if (!height) return

      const kioskProperty = getFeatureProperties(kiosk)

      /**
       *  /\_/\            HAPPITAT TEMP                /\_/\
       * ( o.o ) Set unit and kiosk color to `#D9D0C3` ( o.o )
       *  > ^ <    for floors that are not available    > ^ <
       */
      const { venue, ordinal } = kiosk.properties
      const venueName = venue.properties.name.en

      const selectedFloorBloominas = venueName === "Bloominas" && ordinal >= 4
      const selectedFloorWonderwild =
        venueName === "Wonderwild" && ordinal >= 4 && ordinal < 7
      const selectedFloorFestieTown =
        venueName === "Festie Town" && ordinal >= 4

      if (
        selectedFloorBloominas ||
        selectedFloorWonderwild ||
        selectedFloorFestieTown
      ) {
        kioskProperty.defaultColor = "#D9D0C3"
      }

      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        offset: -0.1,
        altitude: altitude ?? 0,
        bottomHeight: bottomHeight ?? 0,
        topOutline: kioskProperty.topOutline,
        bottomOutline: kioskProperty.bottomOutline,
        outlineColor: kioskProperty.defaultOutlineColor,
      }

      const color = kioskProperty.defaultColor

      if (color === "transparent") return

      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })

      const meshs = createExtrudePolygon(
        kiosk.geometry,
        threeLayer,
        material,
        height,
        kioskProperty,
        options3d
      )

      return meshs
    },
    createExtrudedLevel: (level, threeLayer, options) => {
      const { height } = options || {}
      if (!height) return
      const levelProperty = getFeatureProperties(level)
      const options3d = { offset: 0 }
      const color = levelProperty?.defaultColor || "#808080"
      if (color === "transparent" || _.isEmpty(level?.geometry?.coordinates))
        return
      const material = new MeshLambertMaterial({
        color,
        transparent: true,
      })
      const meshs = createExtrudePolygon(
        level.geometry,
        threeLayer,
        material,
        height,
        levelProperty,
        options3d
      )
      return meshs
    },
    createExtrudedSection: (section, threeLayer, options) => {
      const { is_clickable = true } = section.properties
      const { height, altitude } = options || {}

      if (!height) return

      const sectionProperty = getFeatureProperties(section)
      const {
        topOutline,
        defaultOutlineColor,
        defaultColor,
        opacity,
        defaultLineWidth,
        visible,
      } = sectionProperty

      //NOTE: offset will be dynamic in future improvement
      const options3d = {
        offset: -0.1,
        altitude: altitude ?? 0,
        topOutline,
        outlineColor: defaultOutlineColor,
        linewidth: defaultLineWidth,
        visible,
        renderOrder: 4,
        interactive: is_clickable,
      }

      if (defaultColor === "transparent") return

      const material = new MeshLambertMaterial({
        color: defaultColor,
        transparent: true,
        opacity,
        visible,
        depthTest: false,
      })

      const meshs = createExtrudePolygon(
        section.geometry,
        threeLayer,
        material,
        height,
        sectionProperty,
        options3d
      )

      return meshs
    },
    createAmbientLight: (config) => {
      const { color: colorString = "0xffffff", intensity = 1 } = config
      const color = parseInt(colorString, 16)
      const ambientLight = new AmbientLight(color, intensity)
      return ambientLight
    },
    createDirectionalLight: (config) => {
      const {
        color: colorString = "0xffffff",
        intensity = 1,
        position: positionString = [0, 0, 0],
      } = config
      const color = parseInt(colorString, 16)
      const [x, y, z] = positionString
      const light = new DirectionalLight(color, intensity)
      light.position.set(x, y, z).normalize()
      return light
    },
    createModel3d: async (model3d) => {
      const { id, feature_type, geometry, properties } = model3d
      const {
        model: url,
        bottom_height = 0,
        rotation_x,
        rotation_y,
        rotation_z,
        translation_x,
        translation_y,
        translation_z,
        level,
        scale,
        render_type,
        feature,
        properties: modelProperties,
      } = properties

      const propertiesByRenderType = {
        OUTDOOR_ONLY: {
          uniforms: {
            polygonOpacity: {
              stops: [
                [18.5, 1],
                [19, 0],
              ],
            },
            lineOpacity: {
              stops: [
                [18.5, 1],
                [19, 0],
              ],
            },
          },
        },
      }
      const defaultPropertiesByRenderType =
        propertiesByRenderType[render_type] ?? null

      const object = new GLTFMarker([...geometry.coordinates, bottom_height], {
        symbol: {
          url: url,
          scaleX: scale,
          scaleY: scale,
          scaleZ: scale,
          rotationX: rotation_x || 0,
          rotationY: rotation_y || 0,
          rotationZ: rotation_z || 0,
          translationX: translation_x || 0,
          translationY: translation_y || 0,
          translationZ: translation_z || 0,
          animation: true,
          loop: true,
          ...defaultPropertiesByRenderType,
          ...modelProperties,
        },
      })
      const ordinal = level?.properties?.ordinal || null
      const modelProperty = {
        id,
        feature_type,
        ordinal,
        isAdd: true,
        featureProperties: feature ? getFeatureProperties(feature) : null,
      }

      object.properties = modelProperty
      return object
    },
    create3dEscalator: async (escalator, coordinate, options) => {
      const { id, feature_type } = escalator
      const ordinal = escalator.properties?.ordinal
      const model = {
        up: {
          url: "https://cdn.venue.in.th/static/glb/escalator-02-up.glb",
          entryOffset: 6.72,
        },
        down: {
          url: "https://cdn.venue.in.th/static/glb/escalator-02-down.glb",
          entryOffset: 2,
        },
      }
      const modelProperty = {
        id,
        feature_type,
        ordinal,
        isAdd: true,
      }

      const {
        direction: dir,
        angle,
        relationshipDirection,
        animation = true,
      } = options
      const rotationZ = dir === "up" ? 180 + angle : angle
      const angleRad = (Math.PI * angle) / 180
      const { url, entryOffset } = model[dir]
      const object = new GLTFMarker([...coordinate, 0], {
        symbol: {
          url,
          rotationZ: rotationZ + 90,
          scaleX: 0.2,
          scaleY: 0.2,
          scaleZ: 0.2,
          translationX: entryOffset * Math.cos(angleRad),
          translationY: entryOffset * Math.sin(angleRad),
          animation:
            animation && relationshipDirection === "directed" ? true : false,
          loop: true,
        },
      })
      object.properties = modelProperty

      return object
    },
    create3DSectionLogoMarker,
  }

  return styler
}

const EXCEPT_AMENITY_LOCATION_CATEGORIES = [
  "walkway",
  "nonpublic",
  "parking",
  "opentobelow",
  "unspecified",
]

export const getLocationByAmenity = (feature) => {
  const unit = _.get(feature, "properties.units[0]", null)
  const unitCategory = _.get(unit, "properties.category")
  if (!unit) return feature.id
  return EXCEPT_AMENITY_LOCATION_CATEGORIES.includes(unitCategory)
    ? feature
    : unit
}

export const getLocationByOccupant = (feature) => {
  const kiosk = _.get(feature, "properties.kiosk", null)
  const unit = _.get(feature, "properties.anchor.properties.unit", null)
  return kiosk || unit
}

export const getLocationBySection = (feature) => {
  const kiosk = _.get(feature, "properties.kiosk", null)
  const unit = _.get(feature, "properties.unit", null)
  return kiosk || unit
}

export const getLocationIdByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "kiosk":
    case "unit":
    case "opening":
    case "section":
      return feature.id
    case "amenity":
      return getLocationByAmenity(feature)?.id
    case "occupant":
      return getLocationByOccupant(feature)?.id
    case "promotion":
    case "event":
      return feature.properties?.feature_id
    default:
      return
  }
}

export const getFeatureByLocationId = (id, features = []) => {
  return features?.find((f) => {
    const mainLocationId = getLocationIdByFeature(f)
    const relatedLocationIds = getRelatedLocationIdsByFeature(f)
    return mainLocationId === id || relatedLocationIds.includes(id)
  })
}

export const getFeaturesByLocationId = (id, features = []) => {
  return features?.filter((f) => {
    const mainLocationId = getLocationIdByFeature(f)
    const relatedLocationIds = getRelatedLocationIdsByFeature(f)
    return mainLocationId === id || relatedLocationIds.includes(id)
  })
}

export const isClickableFeature = (feature) => {
  const isClickable = _.get(feature, "properties.is_clickable")
  switch (feature?.feature_type) {
    case "amenity":
      return _.isNull(isClickable) ? true : isClickable
    case "occupant":
      return true
    default:
      return
  }
}

export const getLocationByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getLocationByAmenity(feature)
    case "occupant":
      return getLocationByOccupant(feature)
    case "section":
      return getLocationBySection(feature)
    default:
      return
  }
}
export const getSectionByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "occupant":
    case "unit":
    case "kiosk":
      return feature?.properties.section
    default:
      return
  }
}

export const getMarkerId = (marker) => {
  // Retrieve marker ID via `properties.id` for Marker, or `options.properties.id` for UIMarker.
  return marker?.properties?.id || marker?.options?.properties?.id
}

// Main feature ID is always the first item.
export const parseElementId = (id) => id?.split("_")[0]

/*
 * Utility functions to retrieve related locations for amenities and occupants.
 * - Used for highlighting on the floor plan.
 * - Displays location details.
 */

export const getRelatedLocationsByOccupant = (feature) => {
  const kiosks = _.get(feature, "properties.kiosks", [])
  const units = _.get(feature, "properties.units", [])
  return [...kiosks, ...units]
}

export const getRelatedLocationsBySection = (feature) => {
  const kiosks = _.get(feature, "properties.kiosks", [])
  const units = _.get(feature, "properties.units", [])
  return [...kiosks, ...units]
}

export const getRelatedLocationsByAmenity = (feature) => {
  const units = _.get(feature, "properties.units", [])

  if (units.length === 0) return [feature]

  return units.filter((unit) => {
    const unitCategory = _.get(unit, "properties.category")
    return !EXCEPT_AMENITY_LOCATION_CATEGORIES.includes(unitCategory)
  })
}

export const getRelatedLocationIdsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getRelatedLocationsByAmenity(feature).map((v) => v?.id)
    case "occupant":
      return getRelatedLocationsByOccupant(feature).map((v) => v?.id)
    default:
      return []
  }
}

export const getDuplexGeometryIdsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "occupant":
      return getRelatedLocationsByOccupant(feature).map((location) =>
        buildCompositeId([feature?.id, location?.id], {
          suffix: SUFFIX_MARKER,
        })
      )
    default:
      return []
  }
}

export const getRelatedLocationsByFeature = (feature) => {
  switch (feature?.feature_type) {
    case "amenity":
      return getRelatedLocationsByAmenity(feature)
    case "occupant":
      return getRelatedLocationsByOccupant(feature)
    default:
      return []
  }
}

export const getOrdinalByLocationId = (locationId, feature) => {
  if (!feature) return null

  const mainUnit = _.get(feature, "properties.unit")
  const mainKiosk = _.get(feature, "properties.kiosk")
  const relatedLocations = getRelatedLocationsByFeature(feature)

  const allLocations = [mainUnit, mainKiosk, ...relatedLocations].filter(
    Boolean
  )
  const targetLocation = allLocations.find(
    (location) => location.id === locationId
  )

  return targetLocation
    ? _.get(targetLocation, "properties.level.properties.ordinal") ||
        _.get(targetLocation, "properties.ordinal")
    : null
}
